/* eslint-disable react/prop-types */
import React, {
	useState,
	useImperativeHandle,
	forwardRef,
	useEffect,
	useRef,
	useCallback,
	FC,
} from "react";
import styles from "./ModalSelector.module.css";
import { AgGridReact } from "ag-grid-react";
import GridCheckbox from "../../GridCheckbox";
import CloseIcon from "../../../assets/close-cancel.svg";
import classNames from "classnames";
import { Button, Input } from "antd";
import { RowSelectedEvent } from "ag-grid-community";
import DefaultInput from "../DefaultInput";
import { createDynamicColumnsFromObject } from "../../../utils/grid";
import { MDBModal } from "mdbreact";
import useLang from "../../../hooks/useLang";

interface IModalSelector {
	inputName?: string;
	tableData: any[];
	onRowSelected?: (row: any) => void;
	onRowDeleted?: () => void;
	columnsToShow?: string[];
	buttonLabel?: string;
	columnToSearch?: string;
	childClassName?: string;
	hideSelector?: boolean;
	disabled?: boolean;
	buttonPlaceholder?: string;
	value?: any;
	selectorClassName?: string;
}

const ModalSelector: FC<IModalSelector> = forwardRef(
	(
		{
			inputName,
			tableData,
			onRowSelected,
			onRowDeleted,
			columnsToShow,
			buttonLabel,
			columnToSearch = "id",
			hideSelector = false,
			disabled,
			buttonPlaceholder,
			value,
			selectorClassName,
		},
		ref
	) => {
		const { strings } = useLang("driverPayments");
		buttonPlaceholder = buttonPlaceholder ?? strings?.choose;
		const [columns, setColumns] = useState<any[]>([]);
		const gridRef = useRef<any>();
		const [isOpen, setIsOpen] = useState(false);
		const [buttonDescription, setButtonDescription] = useState<
			string | null
		>();
		const [modalSelectorInput, setModalSelectorInput] = useState<any>(null);
		const articleFocus = useRef<any>();
		const onFilterTextBoxChanged = useCallback((value: any) => {
			gridRef?.current.api.setQuickFilter(value);
		}, []);

		useImperativeHandle(ref, () => ({
			clear: () => {
				setModalSelectorInput(null);
				setButtonDescription(null);
			},
			setRow: (searchTerm: any) => {
				let buttonDescription = "";
				let row = tableData.find(
					(element) => element[columnToSearch] === searchTerm
				);
				if (!row) {
					throw new Error("Row not found");
				}
				columnsToShow?.forEach(
					(element) => (buttonDescription += row[element] + " ")
				);

				setButtonDescription(buttonDescription);
				setModalSelectorInput(row[columnToSearch]);
			},
			focus: () => {
				articleFocus?.current?.focus();
			},
			toggle: () => {
				toggleIsOpen();
			},
		}));
		const toggleIsOpen = () => {
			setIsOpen(!isOpen);
		};

		const onModalSelectorInputChange = (value: any) => {
			setModalSelectorInput(value);
		};
		const onSubmit = (event: any) => {
			event.preventDefault();
			if (!modalSelectorInput) {
				setButtonDescription(null);
				onRowDeleted?.();
				return;
			}
			let row = tableData.find(
				(item) =>
					item[columnToSearch]?.toString() ===
					modalSelectorInput?.toString()
			);
			if (!row) {
				row = tableData?.find((item) => {
					for (const [key, value] of Object.entries(item)) {
						if (
							value
								?.toString()
								.toLowerCase()
								.includes(
									modalSelectorInput?.toString().toLowerCase()
								)
						) {
							return item;
						}
					}
				});
			}
			if (!row) {
				return;
			}
			onRowSelected?.(row);
			setSelectedRowOnComponent(row);
		};

		const setSelectedRowOnComponent = (row: any) => {
			if (!row) {
				setButtonDescription(null);
				setModalSelectorInput(null);
				return;
			}
			let buttonDescription = "";
			columnsToShow?.forEach(
				(element) => (buttonDescription += row[element] + " ")
			);
			setButtonDescription(buttonDescription);
			setModalSelectorInput(row?.[columnToSearch]);
		};
		const rowSelectCallback = ({ node }: RowSelectedEvent) => {
			if (node?.data) {
				onRowSelected?.(node.data);
				setSelectedRowOnComponent(node.data);
			}
			setIsOpen(false);
		};

		const removeSelectedRow = (event: any) => {
			event?.stopPropagation();
			event?.preventDefault();
			setModalSelectorInput(null);
			setButtonDescription(null);
			onRowDeleted?.();
		};

		useEffect(() => {
			const columns = createDynamicColumnsFromObject(tableData?.[0]);
			setColumns(columns);
		}, [tableData, strings]);

		useEffect(() => {
			setSelectedRowOnComponent(value);
		}, [value]);

		return (
			<>
				<MDBModal
					animation="bottom"
					isOpen={isOpen}
					centered
					toggle={toggleIsOpen}
					className={styles.modal}
					contentClassName={styles.modalContent}
				>
					<div className={styles.titleContainer}>
						<div className={styles.title}>{strings?.choose}</div>
						<button onClick={toggleIsOpen}>
							<img
								alt="Close"
								draggable="false"
								className={styles.icon}
								src={CloseIcon}
							/>
						</button>
					</div>
					<div className={styles.searchContainer}>
						<DefaultInput
							className={styles.searchInput}
							placeholder={strings?.search}
							onChange={(e) =>
								onFilterTextBoxChanged(e.target.value)
							}
						/>
					</div>
					<div className={`ag-theme-alpine  ${styles.grid}`}>
						<AgGridReact
							ref={gridRef}
							rowData={tableData}
							columnDefs={columns}
							defaultColDef={defaultColDef}
							rowSelection={"single"}
							components={frameworkComponents}
							onRowSelected={rowSelectCallback}
							cacheQuickFilter
							rowHeight={60}
							headerHeight={52}
						/>
					</div>
					<div className={styles.buttons}>
						<Button
							onClick={toggleIsOpen}
							className={styles.modalButton}
						>
							Close
						</Button>
					</div>
				</MDBModal>
				{!hideSelector && (
					<div
						className={classNames(
							styles.selectorContainer,
							selectorClassName
						)}
					>
						<div
							className={styles.label}
							// @ts-ignore
							htmlFor={inputName ?? "selectorInput"}
						>
							{buttonLabel}
						</div>
						<div className={styles.inputAndButtonSection}>
							<form onSubmit={onSubmit} className={styles.form}>
								<Input
									disabled={disabled}
									className={styles.numberInput}
									placeholder={strings?.search}
									name={inputName ?? "selectorInput"}
									id={inputName ?? "selectorInput"}
									onChange={onModalSelectorInputChange}
									value={modalSelectorInput ?? ""}
									ref={articleFocus}
								/>
							</form>
							<button
								className={classNames(styles.button, {
									[styles.buttonHasValue]:
										!!buttonDescription,
								})}
								onClick={() => {
									toggleIsOpen();
								}}
								type="button"
								disabled={disabled}
							>
								{buttonDescription ?? buttonPlaceholder}
								{modalSelectorInput && (
									<button
										className={styles.clearButton}
										type="button"
										onClick={removeSelectedRow}
									>
										<img src={CloseIcon} />
									</button>
								)}
							</button>
						</div>
					</div>
				)}
			</>
		);
	}
);
const frameworkComponents = { checkboxRenderer: GridCheckbox };

const defaultColDef = {
	flex: 1,
	minWidth: 100,
	sortable: true,
	filter: true,
	wrapHeaderText: true,
	resizable: true,
	filterParams: { excelMode: "windows" },
};

export default ModalSelector;
